import { useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = 'face_lab_theme';
export const themePreferenceOrder: readonly ThemePreference[] = ['system', 'light', 'dark'];

function isThemePreference(value: string | null): value is ThemePreference {
    return value !== null && themePreferenceOrder.includes(value as ThemePreference);
}

function resolvedTheme(preference: ThemePreference): 'light' | 'dark' {
    if (preference !== 'system') return preference;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(preference: ThemePreference): void {
    const theme = resolvedTheme(preference);
    document.documentElement.dataset.siteTheme = theme;
    document.documentElement.dataset.siteThemePreference = preference;
    document.documentElement.style.colorScheme = theme;
    document
        .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
        ?.setAttribute('content', theme === 'dark' ? '#071311' : '#f2f8f5');
}

function readThemePreference(): ThemePreference {
    try {
        const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isThemePreference(storedPreference) ? storedPreference : 'system';
    } catch {
        return 'system';
    }
}

export function useTheme(): { preference: ThemePreference; selectNextTheme: () => void } {
    const [preference, setPreference] = useState<ThemePreference>('system');

    useEffect(() => {
        const storedPreference = readThemePreference();
        setPreference(storedPreference);
        applyTheme(storedPreference);
    }, []);

    useEffect(() => {
        if (preference !== 'system') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (): void => {
            applyTheme('system');
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, [preference]);

    return {
        preference,
        selectNextTheme: () => {
            setPreference((current) => {
                const currentIndex = themePreferenceOrder.indexOf(current);
                const nextPreference =
                    themePreferenceOrder[(currentIndex + 1) % themePreferenceOrder.length] ??
                    'system';
                try {
                    window.localStorage.setItem(THEME_STORAGE_KEY, nextPreference);
                } catch {
                    // The selected theme still applies for the current page.
                }
                applyTheme(nextPreference);
                return nextPreference;
            });
        },
    };
}
